import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { MaskLines, easeLux } from "../../lib/motion";
import { scrollToId } from "./SmoothScroll";

const HERO_IMAGES = [
  "/hero-1.jpg",
  "/hero-2.jpg",
  "/hero-3.jpg",
  "/hero-4.jpg",
  "/hero-5.jpg",
  "/hero-6.jpg",
  "/hero-7.jpg",
];

export const Hero = () => {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setIdx((p) => (p + 1) % HERO_IMAGES.length), 6500);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden"
    >
      {/* Parallax ethereal crossfading background */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
        aria-hidden
      >
        {HERO_IMAGES.map((src, i) => (
          <motion.img
            key={src}
            src={src}
            alt=""
            initial={false}
            animate={{ opacity: i === idx ? 0.75 : 0 }}
            transition={{ duration: 2.4, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ))}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--es-bg)_0%,var(--es-bg)_38%,transparent_64%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_58%,var(--es-bg)_100%)]" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-12 pb-16 lg:pb-24 pt-36">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 1 }}
          className="text-[11px] uppercase tracking-[0.4em] text-es-muted-fg mb-8"
          data-testid="hero-overline"
        >
          EverSoul.world · Provisional patent pending
        </motion.p>

        <h1 className="font-serif font-light text-es-fg tracking-tighter leading-[0.92] text-[clamp(3rem,11vw,10rem)]">
          <MaskLines lines={["Your legacy,", "orchestrated."]} delay={0.25} />
        </h1>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1, ease: easeLux }}
            className="lg:col-span-6 text-base sm:text-lg text-es-muted-fg leading-relaxed max-w-xl"
            data-testid="hero-subtitle"
          >
            EverSoul is a consent governed digital legacy and identity platform designed
            to preserve a person's memories, voice, likeness, values, and future guidance
            within boundaries they define during life. It gives individuals control over
            whether and how their personal AI may continue, while keeping legal and
            fiduciary authority in human hands.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 1, ease: easeLux }}
            className="lg:col-span-6 lg:col-start-8 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-vision-btn"
              onClick={() => scrollToId("manifesto")}
              className="group relative px-7 py-4 rounded-full bg-es-fg text-es-primary-fg text-sm tracking-wide overflow-hidden"
            >
              <span className="relative z-10">Explore the Vision</span>
            </button>
            <button
              data-testid="hero-invest-btn"
              onClick={() => scrollToId("invest")}
              className="group px-7 py-4 rounded-full border border-es-fg/30 text-es-fg text-sm tracking-wide hover:border-es-fg transition-colors duration-300 flex items-center gap-2"
            >
              Investor &amp; Partnership Brief
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">→</span>
            </button>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1.2, ease: easeLux }}
          data-testid="hero-principle"
          className="mt-14 font-serif italic text-es-muted-fg/90 text-lg sm:text-xl tracking-tight"
        >
          Consent before continuation.
        </motion.p>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.4em] text-es-muted-fg"
      >
        Scroll
      </motion.div>
    </section>
  );
};
